import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../components/context/Auth";
import SlidingFilter2 from "../../components/updatedComponents/Dealers/SlidingFilter2";
import InputTypedate from "../../components/updatedComponents/FormComponents/InputTypedate";
import Pagination2 from "../../components/updatedComponents/FormComponents/Pagination2";
import TopTabBar from "../../components/updatedComponents/Tanolet/TopTabBar";
import styles from "./Marketing2.module.css";
import DealerStatusCard from "../../components/updatedComponents/Dealers/DealerStatusCard";
import SearchBar from "../../components/updatedComponents/FormComponents/SearchBar";
import NoDataFound from "../../components/updatedComponents/NoDataFound/NoDataFound";
import { CreateTem, TemplateNotFound } from "../../components/icons/Icon2";
import Skeleton from "../../components/updatedComponents/Skeleton/Skeleton";
import fetchData from "../../APIsControll/apiControll";
import InputRedio from "../../components/updatedComponents/FormComponents/InputRedio";
import Button from "../../components/updatedComponents/Dealers/Button";
import ModalWrapper from "../../components/updatedComponents/layoutWrapper/ModalWrapper";
import CreateLeadPopUp from "./CreateLeadPopUp";
import useTableHeight from "../../hooks/useTableHeight";
import { formatDateTime, getMinDate, getStatusColor } from "../../Utils/Utils";
import useDebounce from "../../hooks/useDebounce";
import {
  daysData,
  leadTypeData,
  marketingFilterData,
  timeStampsData,
} from "../../constants/constants";
import CloseCompleteLead from "./CloseCompleteLead";
import AssignLeadModal from "./AssignLeadModal";
import ShareLeadModal from "./ShareLeadModal";
import { CryptoDecrypt } from "../../Utils/TextUtils";

const Marketing = () => {
  const { mode, managerId, designations } = useAuth();

  const tableRef = useRef(null);

  const limit = useTableHeight(tableRef);

  const [daysTab, setDaysTab] = useState("today");
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("All");
  const [statusData, setStatusData] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [leadType, setLeadType] = useState("all");
  const [timeStamps, setTimeStamps] = useState("updatedAt");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managerAuth, setManagerAuth] = useState({});
  const [selectedRadio, setSelectedRadio] = useState({
    label: "Phone No",
    value: "mobile",
  });

  const isSuperAdmin = designations.includes("superadmin");
  const debouncedKeyword = useDebounce(keyword, 700);
  const minDate = getMinDate();
  const canViewRetail = managerAuth.retailAccess;
  const canViewWholeSale = managerAuth.wholeSaleAccess;

  const getLeads = useCallback(async () => {
    if (daysTab === "period" && (startDate === "" || endDate === "")) return;

    const apiData = {
      managerId: managerId,
      keyword: debouncedKeyword?.trim(),
      leadType: leadType,
      day: daysTab === "period" ? "" : daysTab,
      startDate: startDate,
      endDate: endDate,
      leadStatus: status,
      serachBy: selectedRadio.value,
      timeStamps: timeStamps,
    };
    setLoading(true);
    try {
      const res = await fetchData(
        `leads/getLeads?page=${page}&limit=${limit}`,
        apiData,
      );
      setLoading(false);
      setData(res.data);
      setPages(res.pages);
      setStatusData(res.leads || []);
      setManagers(
        res.managers.map((m) => ({
          label: m.name,
          value: m.managerId,
        })),
      );
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  }, [
    daysTab,
    startDate,
    endDate,
    managerId,
    debouncedKeyword,
    leadType,
    status,
    selectedRadio.value,
    timeStamps,
    page,
    limit,
  ]);

  const getLead = async (lead) => {
    const apiData = {
      managerId: managerId,
      leadId: lead.leadId,
    };

    try {
      const res = await fetchData("leads/getLead", apiData);

      if (res.status === "success") {
        if (lead.type === "wholesale") {
          if (lead.status === "New") {
            openAssignLeadModal(res.lead);
          }
          if (lead.status === "Pending") {
            openCloseCompleteLeadModal(res.lead);
          }
          if (lead.status === "Completed" || lead.status === "Closed") {
            openCompletionModal(res.lead);
          }
        } else {
          if (lead.status === "New") {
            openAssignLeadModal(res.lead);
          }
          if (lead.status === "Pending" || lead.status === "Sent") {
            openShareLeadModal(res.lead);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (managerId && limit !== 1) {
      getLeads();
    }
  }, [getLeads, limit, managerId]);

  useEffect(() => {
    if (selectedRadio.value === "storeName") {
      if (leadType === "retail") {
        setSelectedRadio({ label: "Contact", value: "mobile" });
      }
    }
  }, [leadType, selectedRadio.value]);

  useEffect(() => {
    if (isSuperAdmin) {
      setLeadType("all");
    } else {
      if (canViewRetail && canViewWholeSale) {
        setLeadType("all");
      } else if (canViewRetail) {
        setLeadType("retail");
      } else {
        setLeadType("wholesale");
      }
    }
  }, [canViewRetail, canViewWholeSale, isSuperAdmin]);

  useEffect(() => {
    const encrypted = localStorage.getItem("marketingManagerAuth");

    if (encrypted) {
      try {
        // const bytes = CryptoJS.AES.decrypt(encrypted, "secret key");
        const decrypted = JSON.parse(CryptoDecrypt(encrypted));

        setManagerAuth(decrypted);
      } catch (error) {
        console.error("Failed to decrypt managerAuth:", error);
      }
    }
  }, []);

  const daysTabChange = (label) => {
    const selectedFilter = daysData.find((item) => item.label === label);
    const newDayTab = selectedFilter?.value || "today";

    setDaysTab(newDayTab);

    if (newDayTab !== "period") {
      setPages([]);
      setStartDate("");
      setEndDate("");
      setPage(1);
    }
  };

  const leadTypeChange = (label) => {
    const selectedFilter = leadTypeData?.find((item) => item?.label === label);
    const newLeadType = selectedFilter?.value || "all";

    setLeadType(newLeadType);
  };

  const timeStampsChange = (label) => {
    const selectedFilter = timeStampsData?.find(
      (item) => item?.label === label,
    );
    const newTimeStamps = selectedFilter?.value || "updatedAt";

    setTimeStamps(newTimeStamps);
  };

  const openModal = (content) => {
    setIsModalOpen(true);
    setModalContent(content);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  const openCreateLeadPopUp = () => {
    openModal(
      <CreateLeadPopUp
        mode={mode}
        close={closeModal}
        managerId={managerId}
        getLeads={getLeads}
      />,
    );
  };

  const openCompletionModal = (lead) => {
    openModal(
      <CloseCompleteLead
        mode={mode}
        closeModal={closeModal}
        managerId={managerId}
        lead={lead}
        getLeads={getLeads}
        flag="view"
      />,
    );
  };

  const openCloseCompleteLeadModal = (lead) => {
    openModal(
      <CloseCompleteLead
        mode={mode}
        closeModal={closeModal}
        managerId={managerId}
        lead={lead}
        getLeads={getLeads}
        flag=""
      />,
    );
  };

  const openAssignLeadModal = (lead) => {
    openModal(
      <AssignLeadModal
        mode={mode}
        closeModal={closeModal}
        managers={managers}
        managerId={managerId}
        lead={lead}
        getLeads={getLeads}
        isSuperAdmin={isSuperAdmin}
        openCloseCompleteLeadModal={openCloseCompleteLeadModal}
        openShareLeadModal={openShareLeadModal}
      />,
    );
  };

  const openShareLeadModal = (lead) => {
    openModal(
      <ShareLeadModal
        mode={mode}
        closeModal={closeModal}
        managerId={managerId}
        leadId={lead.leadId}
        getLeads={getLeads}
        lead={lead}
        setModalContent={setModalContent}
      />,
    );
  };

  const allStatus = Array.isArray(statusData)
    ? marketingFilterData.map((filterOption) => {
        const matchedStatus = statusData.find(
          (status) => status.status === filterOption.status,
        );
        return {
          ...filterOption,
          count: matchedStatus?.count || 0,
        };
      })
    : [];

  // console.log("canViewWholeSale", canViewWholeSale);
  // console.log("canViewRetail", canViewRetail);

  return (
    <>
      <ModalWrapper isOpen={isModalOpen} onClose={closeModal}>
        {modalContent}
      </ModalWrapper>
      <div
        className={styles.marketing}
        style={
          mode ? { backgroundColor: "#2C2E33", borderColor: "#2C2E33" } : {}
        }
      >
        <div className={styles.top}>
          <div>
            <TopTabBar truncate={false} text={"Leads"} />
          </div>
          <div className={styles.top_right}>
            <SlidingFilter2
              itemList={daysData.map((item) => item.label)}
              setFilter={daysTabChange}
              selectedItem={
                daysData.find((item) => item.value === daysTab)?.label
              }
            />
            {daysTab === "period" && (
              <>
                <InputTypedate
                  text={"From"}
                  value={startDate}
                  max={endDate}
                  min={minDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (daysTab !== "period") {
                      // setDaysTab("");
                    }
                    // setStatus("");
                  }}
                />
                {startDate !== "" && (
                  <InputTypedate
                    text={"To"}
                    value={endDate}
                    min={startDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (daysTab !== "period") {
                        // setDaysTab("");
                      }
                      // setStatus("");
                    }}
                  />
                )}
              </>
            )}

            <Pagination2
              mode={mode}
              currentPage={page}
              setCurrentPage={setPage}
              totalPages={pages}
            />
          </div>
        </div>

        <div>
          <div
            className={styles.filters}
            style={
              mode ? { borderColor: "#232529", backgroundColor: "#232529" } : {}
            }
          >
            <>
              {allStatus?.map((s) => (
                <div
                  key={s.status}
                  className={`${styles.filter} ${
                    mode ? styles.filter_dark : ""
                  } ${
                    s.status === status
                      ? mode
                        ? styles.filter_active_dark
                        : styles.filter_active
                      : ""
                  }`}
                  onClick={async () => {
                    if (s.status === status) {
                      await getLeads();
                    } else {
                      setStatus(s.status);
                      setPage(1);
                    }
                  }}
                >
                  <DealerStatusCard
                    statusText={s.label}
                    statusItemCount={s.count}
                  />
                </div>
              ))}
            </>
          </div>
        </div>

        <div className={styles.rem_filters}>
          <div className={styles.rem_filters_left}>
            <SearchBar
              padding={"6px"}
              fontSize={"12px"}
              fontWeight={"400"}
              borderRadius={"5px"}
              placeholder={selectedRadio.label}
              value={keyword}
              type={selectedRadio.value === "mobile" ? "number" : "text"}
              maxLength={selectedRadio.value === "mobile" ? 10 : 1000}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              onClear={(e) => {
                setKeyword("");
              }}
            />
            <InputRedio
              lableText="Contact"
              name="mobile"
              id="mobile"
              value="mobile"
              checked={selectedRadio.value === "mobile"}
              onChange={() =>
                setSelectedRadio({
                  label: "Contact",
                  value: "mobile",
                })
              }
            />
            <InputRedio
              lableText="Name"
              name="name"
              id="name"
              value="name"
              checked={selectedRadio.value === "name"}
              onChange={() =>
                setSelectedRadio({
                  label: "Name",
                  value: "name",
                })
              }
            />
            {(leadType === "wholesale" || leadType === "all") && (
              <InputRedio
                lableText="Company Name"
                name="storeName"
                id="storeName"
                value="storeName"
                checked={selectedRadio.value === "storeName"}
                onChange={() =>
                  setSelectedRadio({
                    label: "Company Name",
                    value: "storeName",
                  })
                }
              />
            )}
          </div>
          <div className={styles.rem_filters_right}>
            <SlidingFilter2
              itemList={timeStampsData.map((item) => item.label)}
              setFilter={timeStampsChange}
              selectedItem={
                timeStampsData.find((t) => t.value === timeStamps)?.label
              }
            />

            {(isSuperAdmin || (canViewRetail && canViewWholeSale)) && (
              <SlidingFilter2
                itemList={leadTypeData.map((item) => item.label)}
                setFilter={leadTypeChange}
                selectedItem={
                  leadTypeData.find((role) => role.value === leadType)?.label
                }
              />
            )}

            {/* {isSuperAdmin && ( */}
            <Button
              buttonText={"Add Lead"}
              buttonIcon={<CreateTem />}
              buttonBgColor={"#4164E3"}
              buttonTextColor={"#FFFFFF"}
              buttonClick={openCreateLeadPopUp}
            />
            {/* )} */}
          </div>
        </div>

        <ul className={styles.data} ref={tableRef}>
          <li
            className={`${styles.data_header} ${
              mode ? styles.data_header_dark : ""
            }`}
          >
            <div className={styles.entry}>Name</div>
            <div className={styles.entry}>Contact</div>
            <div className={styles.entry}>Address</div>
            {leadType === "wholesale" && (
              <div className={styles.entry}>Company</div>
            )}
            {leadType === "retail" && (
              <div className={styles.entry}>Share Count</div>
            )}
            {leadType === "all" && <div className={styles.entry}>Type</div>}
            <div className={styles.entry}>Status</div>
            <div className={styles.entry}>Assigned To</div>
            <div className={styles.entry}>Created By</div>
          </li>

          <div className={styles.data_list}>
            {loading ? (
              <div>
                <Skeleton
                  lines={15}
                  thickness={5}
                  additionalStyles={{ marginBottom: "0px", marginTop: "0px" }}
                />
              </div>
            ) : (
              <>
                {!loading && data?.length === 0 ? (
                  <>
                    <div className={styles.no_data}>
                      <NoDataFound
                        icon={<TemplateNotFound />}
                        textTitle={`No Data Found`}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {data?.map((d, index) => (
                      <div key={d.leadId}>
                        <li
                          className={`${styles.data_row} ${
                            mode ? styles.data_row_dark : ""
                          }`}
                          key={d.leadId + index}
                          onClick={() => getLead(d)}
                        >
                          <div className={styles.entry} title={d.name}>
                            {d.name}
                          </div>

                          <div className={styles.entry} title={d.mobile}>
                            {d.mobile}
                          </div>
                          <div
                            className={styles.entry}
                            title={
                              d.pincode +
                              " " +
                              d.city +
                              " " +
                              (d.district || "") +
                              " " +
                              d.state
                            }
                          >
                            {d.pincode +
                              " " +
                              d.city +
                              " " +
                              (d.district || "") +
                              " " +
                              d.state}
                          </div>
                          {leadType === "wholesale" && (
                            <div className={styles.entry} title={d.storeName}>
                              {d.storeName}
                            </div>
                          )}
                          {leadType === "retail" && (
                            <div className={styles.entry} title={d.shareCount}>
                              {d.shareCount}
                            </div>
                          )}
                          {leadType === "all" && (
                            <div className={styles.entry} title={d.type}>
                              {d.type}
                            </div>
                          )}
                          <div
                            className={styles.entry}
                            style={{ color: getStatusColor(d.status) }}
                            title={d.status}
                          >
                            {d.status}
                          </div>
                          <div
                            className={styles.entry}
                            title={d.assignedManager}
                          >
                            {d.assignedManager}
                          </div>
                          <div
                            className={styles.entry}
                            title={
                              (d.source === "admin" ? d.createdBy : d.source) +
                              " " +
                              formatDateTime(d.createdAt)
                            }
                          >
                            {d.source === "admin" ? d.createdBy : d.source}{" "}
                            {formatDateTime(d.createdAt)}
                          </div>
                        </li>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </ul>
      </div>
    </>
  );
};

export default Marketing;
